import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { fmtISO, fmtLong, fromISO } from '../lib/format.js';
import { ConfirmDialog } from './ConfirmDialog.jsx';
import { Icon } from './Icon.jsx';
import { useToast } from './Toast.jsx';

/**
 * v2.0 (B7 admin) — Notifications admin view for super-approvers.
 *
 * Two sub-tabs:
 *   • Today's run — status hero card, four counters, and a per-employee
 *     queue table for the rows the cron is operating on today.
 *   • History — monthly heatmap (default) or detail table layout, with
 *     a ~3-month-back window.
 *
 * Mounted from App.jsx; gated by `role === 'super_approver'` upstream.
 * Server enforces the same gate, so a non-super-approver fetching these
 * endpoints directly gets a 403.
 */

const NOTIFY_HOUR_LOCAL = 12;
const FLAG_EMOJI = { PH: '🇵🇭', PL: '🇵🇱' };
const MONTH_NAMES_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DOW_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
// Visible date range for the history picker. Server caps at 90 days; we
// expose roughly 3 months back to stay well within that.
const HISTORY_MONTHS_BACK = 2;
const POLL_INTERVAL_MS = 45_000;

const STATUS_DEFS = {
  sent:           { label: 'Sent',    fg: '#B57651', bg: '#FFE3D3', dot: '#FFA672' },
  skipped_no_gap: { label: 'No gap',  fg: '#026153', bg: '#CBFAF3', dot: '#05E6C5' },
  pending:        { label: 'Pending', fg: '#065986', bg: '#E0F2FE', dot: '#065986' },
  error:          { label: 'Error',   fg: '#B30745', bg: '#FFE7EF', dot: '#B30745' },
};

// notify_log encodes errors as "error:<reason-prefix>" (40-char cap on
// the suffix). Normalise into a flat status + optional message so the
// rest of the view can assume four discrete states.
function normaliseEmployee(emp) {
  const raw = String(emp.status || 'pending');
  if (raw === 'sent' || raw === 'skipped_no_gap' || raw === 'pending') {
    return { ...emp, status: raw };
  }
  if (raw.startsWith('error:')) {
    return { ...emp, status: 'error', errorMsg: raw.slice('error:'.length) };
  }
  if (raw === 'error') return { ...emp, status: 'error' };
  // Any other value (future status code) — surface as-is in the pill
  // tooltip but bucket as 'error' so it doesn't quietly drop.
  return { ...emp, status: 'error', errorMsg: raw };
}

function fmtTimeLocal(iso) {
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
}

function fmtDateShort(iso) {
  try {
    return fromISO(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function isoIsWeekend(iso) {
  const d = fromISO(iso);
  const dow = d.getDay();
  return dow === 0 || dow === 6;
}

function StatusPillN({ status, errorMsg }) {
  const def = STATUS_DEFS[status] || STATUS_DEFS.pending;
  return (
    <span
      title={errorMsg || def.label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 9px 3px 8px',
        borderRadius: 999,
        background: def.bg,
        color: def.fg,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: def.dot,
        }}
      />
      {def.label}
    </span>
  );
}

function StatusHero({ status, onRunNow, runBusy, autoRefreshing }) {
  const enabled = !!status.notifyEnabled;
  const last = status.lastCronResult;
  const phNoon = `${status.notifyHourLocal || NOTIFY_HOUR_LOCAL}:00 Manila`;
  const plNoon = `${status.notifyHourLocal || NOTIFY_HOUR_LOCAL}:00 Warsaw`;

  const allEmps = [
    ...(status.teams?.PH?.employees || []),
    ...(status.teams?.PL?.employees || []),
  ];
  const counts = {
    skip: allEmps.filter((e) => e.status === 'skipped_no_gap').length,
    sent: allEmps.filter((e) => e.status === 'sent').length,
    pending: allEmps.filter((e) => e.status === 'pending').length,
    errors: allEmps.filter((e) => e.status === 'error').length,
  };
  const total = allEmps.length;
  const noActivity = total === 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div
      className="card"
      style={{
        padding: '24px 28px',
        borderRadius: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 22,
        background: '#fff',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: 220 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--sp-fg-3)',
              marginBottom: 6,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            Today's run · {fmtLong(today)}
            {autoRefreshing && (
              <span
                className="tt-spinner sm"
                style={{ color: 'var(--sp-fg-4)' }}
                aria-label="Refreshing"
              />
            )}
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: enabled ? '#05E6C5' : '#A1A1A1',
                boxShadow: enabled
                  ? '0 0 0 4px rgba(5,230,197,0.18)'
                  : '0 0 0 4px rgba(161,161,161,0.18)',
              }}
            />
            <span
              style={{
                fontSize: 22,
                fontWeight: 700,
                letterSpacing: '-0.01em',
                color: 'var(--sp-fg-1)',
              }}
            >
              {enabled ? 'Notifications active' : 'Notifications disabled'}
            </span>
          </div>
          <div
            style={{
              marginTop: 8,
              fontSize: 13,
              color: 'var(--sp-fg-2)',
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <span>
              <span style={{ color: 'var(--sp-fg-3)' }}>Scheduled</span>{' '}
              <span style={{ fontWeight: 600 }}>{phNoon}</span>
              <span style={{ color: 'var(--sp-fg-4)' }}> · </span>
              <span style={{ fontWeight: 600 }}>{plNoon}</span>
            </span>
            <span>
              <span style={{ color: 'var(--sp-fg-3)' }}>Last ran</span>{' '}
              <span style={{ fontWeight: 600 }}>
                {last?.ranAt
                  ? `${fmtTimeLocal(last.ranAt)} local`
                  : 'Not yet today'}
              </span>
            </span>
            {last && last.errors > 0 && (
              <span
                style={{
                  color: '#B30745',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '1px 8px',
                  background: '#FFE7EF',
                  borderRadius: 999,
                }}
              >
                <Icon name="alert" size={12} stroke={2.2} />
                {last.errors} delivery error{last.errors === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>
        <button
          className="btn btn-primary btn-lg"
          onClick={onRunNow}
          disabled={!enabled || runBusy}
          title={
            !enabled
              ? 'NOTIFY_ENABLED = false'
              : 'Fire the cron for both teams now — idempotent'
          }
          style={{
            whiteSpace: 'nowrap',
            ...(!enabled || runBusy
              ? { opacity: 0.6, cursor: 'not-allowed' }
              : {}),
          }}
        >
          {runBusy ? (
            <>
              <span className="tt-spinner sm" style={{ color: '#fff' }} />
              &nbsp;Running…
            </>
          ) : (
            <>
              <Icon name="flow" size={15} stroke={2.2} />
              &nbsp;Run notifications now
            </>
          )}
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 12,
        }}
      >
        {[
          { key: 'skip',    label: 'No gap',  value: counts.skip,    fg: '#026153', dot: '#05E6C5', icon: 'check' },
          { key: 'sent',    label: 'Sent',    value: counts.sent,    fg: '#B57651', dot: '#FFA672', icon: 'mail' },
          { key: 'pending', label: 'Pending', value: counts.pending, fg: '#065986', dot: '#065986', icon: 'clock' },
          { key: 'errors',  label: 'Errors',  value: counts.errors,  fg: '#B30745', dot: '#B30745', icon: 'alert' },
        ].map((s) => {
          const active = s.value > 0;
          return (
            <div
              key={s.key}
              style={{
                padding: '14px 16px',
                borderRadius: 12,
                border: '1px solid var(--sp-border)',
                background: '#fff',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: 'var(--sp-fg-3)',
                }}
              >
                <span
                  style={{
                    color: active ? s.dot : 'var(--sp-fg-4)',
                    display: 'inline-flex',
                  }}
                >
                  <Icon name={s.icon} size={12} stroke={2.2} />
                </span>
                {s.label}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 6,
                }}
              >
                <span
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: active ? s.fg : 'var(--sp-fg-4)',
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1,
                  }}
                >
                  {s.value}
                </span>
                {total > 0 && (
                  <span
                    style={{
                      fontSize: 12,
                      color: 'var(--sp-fg-3)',
                      fontWeight: 500,
                    }}
                  >
                    of {total}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {noActivity && (
        <div
          style={{
            marginTop: -8,
            fontSize: 13,
            color: 'var(--sp-fg-3)',
            textAlign: 'center',
          }}
        >
          {enabled
            ? 'No employees in scope today — both teams are off or no one is opted in.'
            : 'Cron is disabled (NOTIFY_ENABLED=false). No employees in scope.'}
        </div>
      )}
    </div>
  );
}

const QUEUE_GRID_COLS = '32px 1.8fr 0.7fr 1fr 0.9fr';

function QueueTableRow({ entry, gridCols, onClick }) {
  const isErr = entry.status === 'error';
  return (
    <div
      onClick={onClick}
      style={{
        display: 'grid',
        gridTemplateColumns: gridCols,
        alignItems: 'center',
        padding: '12px 18px',
        cursor: 'pointer',
        borderBottom: '1px solid var(--sp-border-soft)',
        background: isErr ? 'rgba(255,231,239,0.4)' : '#fff',
        transition: 'background .1s',
      }}
      onMouseEnter={(e) => {
        if (!isErr) e.currentTarget.style.background = 'var(--sp-bg-page)';
      }}
      onMouseLeave={(e) => {
        if (!isErr) e.currentTarget.style.background = '#fff';
      }}
    >
      <div
        className="tt-avatar"
        style={{
          width: 26,
          height: 26,
          fontSize: 10,
          background: entry.team === 'PH' ? '#E6FDF9' : '#E0F2FE',
          color: entry.team === 'PH' ? '#007C7C' : '#065986',
        }}
      >
        {(entry.name || entry.email || '?')
          .split(' ')
          .map((s) => s[0])
          .slice(0, 2)
          .join('')}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--sp-fg-1)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {entry.name || entry.email}
        </div>
        <div
          style={{
            fontSize: 12,
            color: isErr ? '#B30745' : 'var(--sp-fg-3)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: isErr ? 500 : 400,
          }}
        >
          {isErr && entry.errorMsg ? entry.errorMsg : entry.email}
        </div>
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--sp-fg-2)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span aria-hidden="true">{FLAG_EMOJI[entry.team] || ''}</span>{' '}
        {entry.team}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--sp-fg-2)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {entry.department || '—'}
      </div>
      <div>
        <StatusPillN status={entry.status} errorMsg={entry.errorMsg} />
      </div>
    </div>
  );
}

function TodayQueueSection({ status, departmentByEmail, onRowClick }) {
  const [filter, setFilter] = useState('attention');
  const [teamFilter, setTeamFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  const allRows = useMemo(() => {
    const rows = [];
    for (const t of ['PH', 'PL']) {
      const td = status.teams?.[t];
      if (!td) continue;
      if (!td.workingDay) continue;
      for (const e of td.employees || []) {
        const dept = departmentByEmail.get(String(e.email).toLowerCase()) || '—';
        rows.push({
          ...e,
          team: t,
          dateChecked: td.dateChecked,
          department: dept,
        });
      }
    }
    return rows;
  }, [status, departmentByEmail]);

  const baseFiltered = useMemo(
    () =>
      allRows.filter((r) => {
        if (teamFilter !== 'all' && r.team !== teamFilter) return false;
        if (search) {
          const q = search.toLowerCase();
          const name = (r.name || '').toLowerCase();
          const email = (r.email || '').toLowerCase();
          if (!name.includes(q) && !email.includes(q)) return false;
        }
        return true;
      }),
    [allRows, teamFilter, search],
  );

  const counts = useMemo(
    () => ({
      all: baseFiltered.length,
      error: baseFiltered.filter((r) => r.status === 'error').length,
      pending: baseFiltered.filter((r) => r.status === 'pending').length,
      sent: baseFiltered.filter((r) => r.status === 'sent').length,
      skipped_no_gap: baseFiltered.filter((r) => r.status === 'skipped_no_gap').length,
      attention: baseFiltered.filter(
        (r) => r.status === 'error' || r.status === 'pending',
      ).length,
    }),
    [baseFiltered],
  );

  const visible = useMemo(() => {
    if (filter === 'all') return baseFiltered;
    if (filter === 'attention')
      return baseFiltered.filter(
        (r) => r.status === 'error' || r.status === 'pending',
      );
    return baseFiltered.filter((r) => r.status === filter);
  }, [baseFiltered, filter]);

  const sortOrder = { error: 0, pending: 1, sent: 2, skipped_no_gap: 3 };
  const sorted = useMemo(
    () =>
      [...visible].sort(
        (a, b) =>
          (sortOrder[a.status] ?? 9) - (sortOrder[b.status] ?? 9) ||
          (a.name || '').localeCompare(b.name || ''),
      ),
    [visible],
  );

  const DISPLAY_CAP = 20;
  const displayed =
    filter === 'attention' || showAll ? sorted : sorted.slice(0, DISPLAY_CAP);
  const overflowed =
    !showAll && filter !== 'attention' && sorted.length > DISPLAY_CAP;

  const nonWorkingNote = ['PH', 'PL']
    .filter((t) => status.teams?.[t] && !status.teams[t].workingDay)
    .map((t) => ({ team: t, reason: status.teams[t].skipReason }));

  const chips = [
    { k: 'attention',      label: 'Needs attention', count: counts.attention,      dot: '#B30745' },
    { k: 'all',            label: 'All',             count: counts.all,            dot: null },
    { k: 'error',          label: 'Errors',          count: counts.error,          dot: STATUS_DEFS.error.dot },
    { k: 'pending',        label: 'Pending',         count: counts.pending,        dot: STATUS_DEFS.pending.dot },
    { k: 'sent',           label: 'Sent',            count: counts.sent,           dot: STATUS_DEFS.sent.dot },
    { k: 'skipped_no_gap', label: 'No gap',          count: counts.skipped_no_gap, dot: STATUS_DEFS.skipped_no_gap.dot },
  ];

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {chips.map((c) => {
            const active = filter === c.k;
            const disabled =
              c.count === 0 && c.k !== 'all' && c.k !== 'attention';
            return (
              <button
                key={c.k}
                onClick={() => {
                  if (disabled) return;
                  setFilter(c.k);
                  setShowAll(false);
                }}
                disabled={disabled}
                style={{
                  all: 'unset',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 999,
                  fontSize: 13,
                  fontWeight: 600,
                  color: active
                    ? '#fff'
                    : disabled
                    ? 'var(--sp-fg-4)'
                    : 'var(--sp-fg-2)',
                  background: active ? 'var(--sp-fg-1)' : 'var(--sp-bg-muted)',
                  border:
                    '1px solid ' +
                    (active ? 'var(--sp-fg-1)' : 'var(--sp-border)'),
                  opacity: disabled ? 0.55 : 1,
                }}
              >
                {c.dot && (
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: c.dot,
                      opacity: active ? 0.9 : 1,
                    }}
                  />
                )}
                {c.label}
                <span
                  style={{
                    padding: '0 6px',
                    borderRadius: 999,
                    background: active ? 'rgba(255,255,255,0.18)' : '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    color: active ? '#fff' : 'var(--sp-fg-3)',
                  }}
                >
                  {c.count}
                </span>
              </button>
            );
          })}
        </div>
        <div style={{ flex: 1 }} />
        <div
          style={{
            display: 'inline-flex',
            gap: 4,
            padding: 3,
            background: 'var(--sp-bg-muted)',
            borderRadius: 8,
          }}
        >
          {[
            { k: 'all', label: 'Both teams' },
            { k: 'PH', label: '🇵🇭 PH' },
            { k: 'PL', label: '🇵🇱 PL' },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTeamFilter(t.k)}
              style={{
                all: 'unset',
                cursor: 'pointer',
                padding: '4px 10px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                color:
                  teamFilter === t.k ? 'var(--sp-fg-1)' : 'var(--sp-fg-3)',
                background: teamFilter === t.k ? '#fff' : 'transparent',
                boxShadow:
                  teamFilter === t.k
                    ? 'var(--sp-shadow-xs), 0 0 0 1px var(--sp-border)'
                    : 'none',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ position: 'relative', maxWidth: 320 }}>
        <span
          style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--sp-fg-4)',
            pointerEvents: 'none',
          }}
        >
          <Icon name="search" size={14} />
        </span>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="tt-input"
          style={{ paddingLeft: 36, height: 36 }}
        />
      </div>

      {nonWorkingNote.map((n) => (
        <div
          key={n.team}
          style={{
            padding: '10px 14px',
            borderRadius: 10,
            background: '#FFF7E6',
            color: '#A05A00',
            fontSize: 13,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            alignSelf: 'flex-start',
          }}
        >
          <Icon name="cal" size={13} /> {FLAG_EMOJI[n.team]} {n.team} is on{' '}
          {n.reason === 'weekend' ? 'a weekend' : 'a public holiday'} — no run
          scheduled today.
        </div>
      ))}

      {sorted.length === 0 ? (
        <div
          className="card"
          style={{ padding: '36px 20px', textAlign: 'center', borderRadius: 14 }}
        >
          <div style={{ fontSize: 13, color: 'var(--sp-fg-3)' }}>
            {search
              ? `No matches for "${search}".`
              : 'No employees in this view.'}
          </div>
        </div>
      ) : (
        <div
          className="card"
          style={{ padding: 0, borderRadius: 14, overflow: 'hidden' }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: QUEUE_GRID_COLS,
              alignItems: 'center',
              padding: '12px 18px',
              borderBottom: '1px solid var(--sp-border)',
              background: 'var(--sp-bg-page)',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--sp-fg-3)',
            }}
          >
            <div />
            <div>Employee</div>
            <div>Team</div>
            <div>Department</div>
            <div>Status</div>
          </div>
          {displayed.map((e) => (
            <QueueTableRow
              key={`${e.team}:${e.email}`}
              entry={e}
              gridCols={QUEUE_GRID_COLS}
              onClick={() => onRowClick?.({ ...e, iso: e.dateChecked })}
            />
          ))}
          {overflowed && (
            <button
              onClick={() => setShowAll(true)}
              style={{
                all: 'unset',
                cursor: 'pointer',
                width: '100%',
                textAlign: 'center',
                padding: '14px 12px',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--sp-fg-2)',
                borderTop: '1px solid var(--sp-border-soft)',
              }}
              onMouseEnter={(ev) =>
                (ev.currentTarget.style.background = 'var(--sp-bg-page)')
              }
              onMouseLeave={(ev) =>
                (ev.currentTarget.style.background = 'transparent')
              }
            >
              Show {sorted.length - DISPLAY_CAP} more
            </button>
          )}
        </div>
      )}

      {filter === 'attention' &&
        counts.attention === 0 &&
        counts.all > 0 && (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: 12,
              background: '#E6FDF9',
              color: '#026153',
              fontSize: 13,
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              alignSelf: 'flex-start',
            }}
          >
            <Icon name="check" size={14} stroke={2.4} /> All {counts.all}{' '}
            employees evaluated cleanly — nothing to action.
          </div>
        )}
    </section>
  );
}

function MonthPicker({ year, month, onChange, minYear, minMonth, maxYear, maxMonth }) {
  const atFloor = year === minYear && month === minMonth;
  const atCeil = year === maxYear && month === maxMonth;
  const goPrev = () => {
    if (atFloor) return;
    const d = new Date(year, month - 1, 1);
    onChange(d.getFullYear(), d.getMonth());
  };
  const goNext = () => {
    if (atCeil) return;
    const d = new Date(year, month + 1, 1);
    onChange(d.getFullYear(), d.getMonth());
  };
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: 4,
        background: '#fff',
        border: '1px solid var(--sp-border)',
        borderRadius: 10,
      }}
    >
      <button
        onClick={goPrev}
        disabled={atFloor}
        className="btn btn-ghost btn-icon"
        style={{
          width: 28,
          height: 28,
          ...(atFloor ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
        }}
        title="Previous month"
      >
        <Icon name="chevL" size={14} />
      </button>
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--sp-fg-1)',
          minWidth: 110,
          textAlign: 'center',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {MONTH_NAMES_FULL[month]} {year}
      </span>
      <button
        onClick={goNext}
        disabled={atCeil}
        className="btn btn-ghost btn-icon"
        style={{
          width: 28,
          height: 28,
          ...(atCeil ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
        }}
        title="Next month"
      >
        <Icon name="chevR" size={14} />
      </button>
    </div>
  );
}

function LegendSwatch({ swatch, label }) {
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
    >
      {swatch}
      <span>{label}</span>
    </span>
  );
}

/**
 * Pivot the history days payload into:
 *   • `employees` — union of (email,name,team) tuples that appeared on
 *     any day in the range.
 *   • `cellLookup` — Map keyed by `${email}::${iso}` returning a cell
 *     descriptor: { state, status?, sentAt?, errorMsg? } where state ∈
 *     { evaluated, holiday, weekend, future, missing }.
 *
 * History is one-record-per-(employee, date_checked); we keep the
 * pivot pure so both layouts (heatmap + table) can share it.
 */
function pivotHistory(history, todayIso) {
  const empMap = new Map();
  const cellLookup = new Map();
  const teamDayInfo = new Map(); // `${team}::${iso}` → { workingDay, skipReason }

  for (const day of history.days || []) {
    const iso = day.date;
    for (const team of ['PH', 'PL']) {
      const td = day.teams?.[team];
      if (!td) continue;
      teamDayInfo.set(`${team}::${iso}`, {
        workingDay: !!td.workingDay,
        skipReason: td.skipReason || null,
      });
      for (const rawEmp of td.employees || []) {
        const emp = normaliseEmployee(rawEmp);
        const key = String(emp.email).toLowerCase();
        if (!empMap.has(key)) {
          empMap.set(key, {
            email: emp.email,
            name: emp.name || emp.email,
            team,
          });
        }
        cellLookup.set(`${key}::${iso}`, {
          state: 'evaluated',
          status: emp.status,
          sentAt: emp.sentAt || null,
          errorMsg: emp.errorMsg || null,
        });
      }
    }
  }

  // Build the employee list sorted PH first, then PL, then by name. We
  // use teamDayInfo to render team-level non-working markers per cell.
  const employees = Array.from(empMap.values()).sort((a, b) => {
    if (a.team !== b.team) return a.team === 'PH' ? -1 : 1;
    return (a.name || '').localeCompare(b.name || '');
  });

  const cellFor = (email, iso, team) => {
    const key = `${String(email).toLowerCase()}::${iso}`;
    const evaluated = cellLookup.get(key);
    if (evaluated) return evaluated;
    if (iso > todayIso) return { state: 'future' };
    const info = teamDayInfo.get(`${team}::${iso}`);
    if (info && !info.workingDay) {
      return { state: info.skipReason === 'weekend' ? 'weekend' : 'holiday' };
    }
    return { state: 'missing' };
  };

  return { employees, cellFor };
}

function HeatmapCell({ cell, dayIso, employee, onClick }) {
  const cellSize = 14;
  const baseStyle = {
    width: cellSize,
    height: cellSize,
    borderRadius: 3,
  };

  if (cell.state === 'weekend') return null; // weekends excluded from grid

  if (cell.state === 'future') {
    return (
      <div
        title={`${fmtDateShort(dayIso)} · in the future`}
        style={{
          ...baseStyle,
          background: '#FAFAFA',
          border: '1px dashed var(--sp-border-soft)',
        }}
      />
    );
  }
  if (cell.state === 'holiday') {
    return (
      <div
        title={`${fmtDateShort(dayIso)} · ${FLAG_EMOJI[employee.team]} public holiday`}
        style={{
          ...baseStyle,
          background:
            'repeating-linear-gradient(135deg, #F2F2F2 0 3px, #FAFAFA 3px 6px)',
        }}
      />
    );
  }
  if (cell.state === 'missing') {
    // Past working day for which the cron never ran or no log exists.
    return (
      <div
        title={`${fmtDateShort(dayIso)} · ${employee.name} · cron did not run`}
        style={{
          ...baseStyle,
          background: '#FAFAFA',
          border: '1px dashed var(--sp-border-strong)',
        }}
      />
    );
  }

  const { status, errorMsg } = cell;
  const clickProps = {
    onClick: () =>
      onClick?.({
        email: employee.email,
        name: employee.name,
        team: employee.team,
        iso: dayIso,
        status,
        sentAt: cell.sentAt,
        errorMsg,
      }),
    title: `${fmtDateShort(dayIso)} · ${employee.name} · ${
      STATUS_DEFS[status]?.label || status
    }${errorMsg ? ' — ' + errorMsg : ''}`,
  };
  if (status === 'pending') {
    return (
      <button
        {...clickProps}
        style={{
          all: 'unset',
          cursor: 'pointer',
          display: 'block',
          ...baseStyle,
          background: '#fff',
          border: '1px dashed var(--sp-border-strong)',
        }}
      />
    );
  }
  if (status === 'skipped_no_gap') {
    return (
      <button
        {...clickProps}
        style={{
          all: 'unset',
          cursor: 'pointer',
          display: 'block',
          ...baseStyle,
          background: '#05E6C5',
          border: '1px solid #00C1C1',
        }}
      />
    );
  }
  if (status === 'sent') {
    return (
      <button
        {...clickProps}
        style={{
          all: 'unset',
          cursor: 'pointer',
          display: 'block',
          ...baseStyle,
          background: '#FFCBA8',
          border: '1px solid #FFA672',
        }}
      />
    );
  }
  if (status === 'error') {
    return (
      <button
        {...clickProps}
        style={{
          all: 'unset',
          cursor: 'pointer',
          display: 'block',
          ...baseStyle,
          background: '#B30745',
          border: '1px solid #8a0436',
          position: 'relative',
        }}
      >
        <span
          style={{
            position: 'absolute',
            inset: 0,
            color: '#fff',
            fontSize: 9,
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
          }}
        >
          !
        </span>
      </button>
    );
  }
  return null;
}

function MonthlyHeatmap({ history, year, month, todayIso, onCellClick }) {
  const workdays = useMemo(() => {
    const last = new Date(year, month + 1, 0).getDate();
    const out = [];
    for (let d = 1; d <= last; d++) {
      const dt = new Date(year, month, d);
      const dow = dt.getDay();
      if (dow === 0 || dow === 6) continue;
      out.push({ iso: fmtISO(dt), dom: d, dow });
    }
    return out;
  }, [year, month]);

  const pivot = useMemo(() => pivotHistory(history, todayIso), [history, todayIso]);

  const cellGap = 4;

  const byTeam = useMemo(() => {
    const groups = { PH: [], PL: [] };
    for (const e of pivot.employees) {
      if (groups[e.team]) groups[e.team].push(e);
    }
    return groups;
  }, [pivot]);

  const empRate = (emp) => {
    let total = 0;
    let win = 0;
    for (const d of workdays) {
      if (d.iso > todayIso) continue;
      const cell = pivot.cellFor(emp.email, d.iso, emp.team);
      if (cell.state !== 'evaluated') continue;
      total += 1;
      if (cell.status === 'skipped_no_gap') win += 1;
    }
    return total > 0 ? Math.round((win / total) * 100) : null;
  };

  const renderTeamGroup = (team) => {
    const emps = byTeam[team];
    if (!emps || emps.length === 0) return null;
    const teamHolidayCount = workdays.filter((d) => {
      const cell = emps[0]
        ? pivot.cellFor(emps[0].email, d.iso, team)
        : { state: 'missing' };
      // A team-wide holiday shows as 'holiday' for any employee on that
      // team, so any one employee's cell tells us.
      return cell.state === 'holiday';
    }).length;
    return (
      <React.Fragment key={team}>
        <tr>
          <td colSpan={workdays.length + 2} style={{ padding: '14px 0 6px' }}>
            <div
              style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}
            >
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: 'var(--sp-fg-1)',
                }}
              >
                <span aria-hidden="true" style={{ marginRight: 6 }}>
                  {FLAG_EMOJI[team]}
                </span>{' '}
                {team}
              </span>
              <span style={{ fontSize: 12, color: 'var(--sp-fg-3)' }}>
                {emps.length} employee{emps.length === 1 ? '' : 's'}
              </span>
              {teamHolidayCount > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--sp-fg-3)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  ·{' '}
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      borderRadius: 2,
                      background:
                        'repeating-linear-gradient(135deg, #F2F2F2 0 3px, #FAFAFA 3px 6px)',
                    }}
                  />
                  {teamHolidayCount} {team} holiday
                  {teamHolidayCount === 1 ? '' : 's'} this month
                </span>
              )}
            </div>
          </td>
        </tr>
        {emps.map((emp) => (
          <tr key={`${team}:${emp.email}`}>
            <td
              style={{
                paddingRight: 12,
                fontSize: 12,
                color: 'var(--sp-fg-1)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 200,
              }}
              title={emp.email}
            >
              {emp.name}
            </td>
            {workdays.map((d) => {
              const cell = pivot.cellFor(emp.email, d.iso, emp.team);
              return (
                <td key={d.iso} style={{ padding: 0 }}>
                  <HeatmapCell
                    cell={cell}
                    dayIso={d.iso}
                    employee={emp}
                    onClick={onCellClick}
                  />
                </td>
              );
            })}
            <td
              style={{
                paddingLeft: 10,
                fontSize: 11,
                color: 'var(--sp-fg-3)',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              }}
              title="% of working days with no gap"
            >
              {(() => {
                const r = empRate(emp);
                return r != null ? `${r}%` : '—';
              })()}
            </td>
          </tr>
        ))}
      </React.Fragment>
    );
  };

  return (
    <div
      className="card"
      style={{
        padding: '18px 20px 20px',
        borderRadius: 14,
        overflowX: 'auto',
      }}
    >
      {pivot.employees.length === 0 ? (
        <div
          style={{
            padding: '40px 16px',
            textAlign: 'center',
            color: 'var(--sp-fg-3)',
            fontSize: 14,
          }}
        >
          No notification activity recorded in this month.
        </div>
      ) : (
        <table
          style={{
            borderCollapse: 'separate',
            borderSpacing: `${cellGap}px 3px`,
            fontFamily: 'inherit',
          }}
        >
          <thead>
            <tr>
              <th style={{ width: 180, textAlign: 'left', padding: 0 }} />
              {workdays.map((d) => (
                <th
                  key={d.iso}
                  style={{ width: 14, padding: 0, verticalAlign: 'bottom' }}
                >
                  <div
                    style={{
                      textAlign: 'center',
                      fontSize: 9,
                      fontWeight: 700,
                      color: 'var(--sp-fg-3)',
                      lineHeight: 1.2,
                    }}
                  >
                    <div>{d.dom}</div>
                    <div
                      style={{
                        color: 'var(--sp-fg-4)',
                        fontWeight: 500,
                      }}
                    >
                      {DOW_LETTERS[d.dow]}
                    </div>
                  </div>
                </th>
              ))}
              <th
                style={{
                  padding: '0 0 0 10px',
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: 'var(--sp-fg-3)',
                  textAlign: 'right',
                  verticalAlign: 'bottom',
                }}
              >
                On-track
              </th>
            </tr>
          </thead>
          <tbody>
            {renderTeamGroup('PH')}
            {renderTeamGroup('PL')}
          </tbody>
        </table>
      )}

      <div
        style={{
          marginTop: 18,
          display: 'flex',
          alignItems: 'center',
          gap: 18,
          flexWrap: 'wrap',
          fontSize: 12,
          color: 'var(--sp-fg-2)',
          paddingTop: 14,
          borderTop: '1px solid var(--sp-border-soft)',
        }}
      >
        <LegendSwatch
          label="No gap (logged in full)"
          swatch={
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: 2,
                background: '#05E6C5',
                border: '1px solid #00C1C1',
              }}
            />
          }
        />
        <LegendSwatch
          label="Nudge sent"
          swatch={
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: 2,
                background: '#FFCBA8',
                border: '1px solid #FFA672',
              }}
            />
          }
        />
        <LegendSwatch
          label="Delivery error"
          swatch={
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: 2,
                background: '#B30745',
                border: '1px solid #8a0436',
              }}
            />
          }
        />
        <LegendSwatch
          label="Pending / cron skipped"
          swatch={
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: 2,
                background: '#fff',
                border: '1px dashed var(--sp-border-strong)',
              }}
            />
          }
        />
        <LegendSwatch
          label="Holiday (per team)"
          swatch={
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: 2,
                background:
                  'repeating-linear-gradient(135deg, #F2F2F2 0 3px, #FAFAFA 3px 6px)',
              }}
            />
          }
        />
        <span
          style={{
            color: 'var(--sp-fg-4)',
            marginLeft: 'auto',
            fontSize: 11,
          }}
        >
          Weekends are excluded — no notifications are sent Sat–Sun.
        </span>
      </div>
    </div>
  );
}

function MonthlyTable({ history, year, month, todayIso, onCellClick }) {
  const rows = useMemo(() => {
    const out = [];
    const days = [...(history.days || [])].sort((a, b) =>
      a.date < b.date ? 1 : -1,
    );
    for (const day of days) {
      const iso = day.date;
      const dt = fromISO(iso);
      if (dt.getFullYear() !== year || dt.getMonth() !== month) continue;
      if (isoIsWeekend(iso)) continue;
      if (iso > todayIso) continue;
      for (const team of ['PH', 'PL']) {
        const td = day.teams?.[team];
        if (!td) continue;
        if (!td.workingDay) {
          out.push({
            date: iso,
            team,
            holiday: true,
            holidayName:
              td.skipReason === 'weekend' ? 'Weekend' : 'Public holiday',
          });
          continue;
        }
        for (const rawEmp of td.employees || []) {
          const emp = normaliseEmployee(rawEmp);
          out.push({
            date: iso,
            team,
            email: emp.email,
            name: emp.name || emp.email,
            status: emp.status,
            sentAt: emp.sentAt || null,
            errorMsg: emp.errorMsg || null,
          });
        }
      }
    }
    return out;
  }, [history, year, month, todayIso]);

  const cap = 200;
  const visible = rows.slice(0, cap);
  return (
    <div
      className="card"
      style={{ padding: 0, borderRadius: 14, overflow: 'hidden' }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '110px 60px 1.5fr 1fr 140px',
          alignItems: 'center',
          padding: '10px 16px',
          borderBottom: '1px solid var(--sp-border)',
          background: 'var(--sp-bg-page)',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--sp-fg-3)',
        }}
      >
        <div>Date (D)</div>
        <div>Team</div>
        <div>Employee</div>
        <div>Status</div>
        <div style={{ textAlign: 'right' }}>Sent at</div>
      </div>
      <div style={{ maxHeight: 520, overflowY: 'auto' }}>
        {visible.length === 0 ? (
          <div
            style={{
              padding: '40px 16px',
              textAlign: 'center',
              color: 'var(--sp-fg-3)',
              fontSize: 14,
            }}
          >
            No notification activity recorded in this month.
          </div>
        ) : (
          visible.map((r, i) => (
            <div
              key={`${r.date}:${r.team}:${r.email || 'h'}:${i}`}
              onClick={() => !r.holiday && onCellClick?.({ ...r, iso: r.date })}
              style={{
                display: 'grid',
                gridTemplateColumns: '110px 60px 1.5fr 1fr 140px',
                alignItems: 'center',
                padding: '10px 16px',
                borderBottom: '1px solid var(--sp-border-soft)',
                cursor: r.holiday ? 'default' : 'pointer',
                background: r.holiday
                  ? 'repeating-linear-gradient(135deg, #FAFAFA 0 6px, #F5F5F5 6px 12px)'
                  : '#fff',
                fontSize: 13,
              }}
            >
              <div
                style={{
                  color: 'var(--sp-fg-1)',
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmtDateShort(r.date)}
              </div>
              <div style={{ color: 'var(--sp-fg-2)', fontWeight: 600 }}>
                <span aria-hidden="true" style={{ marginRight: 4 }}>
                  {FLAG_EMOJI[r.team]}
                </span>
                {r.team}
              </div>
              <div style={{ minWidth: 0, overflow: 'hidden' }}>
                {r.holiday ? (
                  <span
                    style={{
                      color: 'var(--sp-fg-3)',
                      fontStyle: 'italic',
                    }}
                  >
                    {r.holidayName} — no run
                  </span>
                ) : (
                  <>
                    <div
                      style={{
                        fontWeight: 600,
                        color: 'var(--sp-fg-1)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.name}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--sp-fg-3)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.email}
                    </div>
                  </>
                )}
              </div>
              <div>
                {!r.holiday && (
                  <StatusPillN status={r.status} errorMsg={r.errorMsg} />
                )}
              </div>
              <div
                style={{
                  textAlign: 'right',
                  color: 'var(--sp-fg-3)',
                  fontSize: 12,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {r.sentAt ? fmtTimeLocal(r.sentAt) + ' local' : '—'}
              </div>
            </div>
          ))
        )}
        {rows.length > visible.length && (
          <div
            style={{
              padding: '12px 16px',
              fontSize: 12,
              color: 'var(--sp-fg-3)',
              textAlign: 'center',
            }}
          >
            Showing first {visible.length} of {rows.length} rows for{' '}
            {MONTH_NAMES_FULL[month]} {year}.
          </div>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
      <span
        style={{
          width: 130,
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          color: 'var(--sp-fg-3)',
        }}
      >
        {label}
      </span>
      <span style={{ flex: 1, fontSize: 14, color: 'var(--sp-fg-1)' }}>
        {value}
      </span>
    </div>
  );
}

function CellDetailDialog({ cell, onClose }) {
  if (!cell) return null;
  return (
    <div className="tt-modal-backdrop" onClick={onClose}>
      <div
        className="tt-modal"
        style={{ maxWidth: 460, marginTop: '15vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '18px 22px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            borderBottom: '1px solid var(--sp-border)',
          }}
        >
          <h2 style={{ flex: 1, margin: 0, fontSize: 16, fontWeight: 700 }}>
            Notification log
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <div
          style={{
            padding: '16px 22px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <DetailRow label="Employee" value={cell.name || cell.email} />
          <DetailRow label="Email" value={cell.email} />
          <DetailRow
            label="Team"
            value={
              <>
                <span aria-hidden="true">{FLAG_EMOJI[cell.team]}</span>&nbsp;
                {cell.team}
              </>
            }
          />
          <DetailRow label="Date checked (D)" value={fmtDateShort(cell.iso)} />
          <DetailRow
            label="Status"
            value={<StatusPillN status={cell.status} errorMsg={cell.errorMsg} />}
          />
          {cell.sentAt && (
            <DetailRow
              label="Sent at"
              value={fmtTimeLocal(cell.sentAt) + ' local'}
            />
          )}
          {cell.errorMsg && (
            <DetailRow
              label="Error"
              value={<span style={{ color: '#B30745' }}>{cell.errorMsg}</span>}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function LayoutSwitch({ layout, onChange }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        gap: 4,
        padding: 4,
        background: 'var(--sp-bg-muted)',
        borderRadius: 10,
      }}
    >
      {[
        { k: 'heatmap', label: 'Heatmap', icon: 'chart' },
        { k: 'table', label: 'Detail table', icon: 'cal' },
      ].map((o) => (
        <button
          key={o.k}
          onClick={() => onChange(o.k)}
          style={{
            all: 'unset',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 7,
            fontSize: 12,
            fontWeight: 600,
            color: layout === o.k ? 'var(--sp-fg-1)' : 'var(--sp-fg-3)',
            background: layout === o.k ? '#fff' : 'transparent',
            boxShadow:
              layout === o.k
                ? 'var(--sp-shadow-xs), 0 0 0 1px var(--sp-border)'
                : 'none',
          }}
        >
          <Icon name={o.icon} size={12} /> {o.label}
        </button>
      ))}
    </div>
  );
}

function SubTab({ active, onClick, icon, label, count }) {
  return (
    <button
      onClick={onClick}
      style={{
        all: 'unset',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 14px',
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 600,
        color: active ? '#fff' : 'var(--sp-fg-2)',
        background: active ? 'var(--sp-fg-1)' : 'transparent',
        transition: 'background .12s, color .12s',
      }}
    >
      <Icon name={icon} size={13} />
      {label}
      {typeof count === 'number' && count > 0 && (
        <span
          style={{
            padding: '0 6px',
            borderRadius: 999,
            background: active ? 'rgba(255,255,255,0.18)' : 'var(--sp-bg-muted)',
            fontSize: 11,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            color: active ? '#fff' : 'var(--sp-fg-2)',
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

export function NotificationsView({ reports }) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const todayIso = fmtISO(today);

  const departmentByEmail = useMemo(() => {
    const m = new Map();
    for (const r of reports || []) {
      m.set(String(r.email).toLowerCase(), r.department || '');
    }
    return m;
  }, [reports]);

  const [tab, setTab] = useState('today');
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState(null);
  const [autoRefreshing, setAutoRefreshing] = useState(false);

  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [layout, setLayout] = useState('heatmap');

  const [runBusy, setRunBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [cellDetail, setCellDetail] = useState(null);

  const toast = useToast();

  const floor = useMemo(() => {
    const d = new Date(today.getFullYear(), today.getMonth() - HISTORY_MONTHS_BACK, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  }, [today]);

  const loadStatus = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) setAutoRefreshing(true);
      else setStatusLoading(true);
      try {
        const data = await api.notificationsStatus();
        const normalised = {
          ...data,
          teams: Object.fromEntries(
            Object.entries(data.teams || {}).map(([k, v]) => [
              k,
              {
                ...v,
                employees: (v.employees || []).map(normaliseEmployee),
              },
            ]),
          ),
        };
        setStatus(normalised);
        setStatusError(null);
      } catch (err) {
        setStatusError(err.message || 'Could not load notifications status');
      } finally {
        if (silent) setAutoRefreshing(false);
        else setStatusLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Poll the snapshot while the Today tab is open. /notifications-status
  // is a cheap read (just notify_log + employees sheet), so 45s gives
  // approvers a near-live view without hammering the cron.
  useEffect(() => {
    if (tab !== 'today') return undefined;
    const id = setInterval(() => loadStatus({ silent: true }), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [tab, loadStatus]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const first = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const last = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      const data = await api.notificationsHistory(first, last);
      setHistory(data);
    } catch (err) {
      setHistoryError(err.message || 'Could not load history');
      setHistory(null);
    } finally {
      setHistoryLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    if (tab !== 'history') return;
    loadHistory();
  }, [tab, loadHistory]);

  const attentionCount = useMemo(() => {
    if (!status) return 0;
    let n = 0;
    for (const t of ['PH', 'PL']) {
      const td = status.teams?.[t];
      if (!td || !td.workingDay) continue;
      for (const e of td.employees || []) {
        if (e.status === 'error' || e.status === 'pending') n += 1;
      }
    }
    return n;
  }, [status]);

  const onRunNow = () => {
    if (!status) return;
    const pending = [
      ...(status.teams?.PH?.employees || []),
      ...(status.teams?.PL?.employees || []),
    ].filter((e) => e.status === 'pending').length;
    setConfirm({
      title: 'Run notifications now?',
      body:
        pending > 0
          ? `Fires the cron for both teams immediately. ${pending} employee${
              pending === 1 ? '' : 's'
            } still pending — already-evaluated employees today won't get a duplicate.`
          : 'All employees have already been evaluated today, so this will be a no-op (idempotent).',
      confirmLabel: 'Run now',
      onConfirm: async () => {
        setRunBusy(true);
        try {
          await api.runNotificationsNow();
          const dispatched = pending;
          toast(
            dispatched > 0
              ? `Cron fired — ${dispatched} delivery attempt${
                  dispatched === 1 ? '' : 's'
                } dispatched`
              : 'Cron fired — no new emails (idempotent)',
          );
          await loadStatus({ silent: true });
        } catch (err) {
          toast(err.message || 'Could not fire the cron', 'error');
        } finally {
          setRunBusy(false);
          setConfirm(null);
        }
      },
    });
  };

  return (
    <div style={{ flex: 1, background: 'var(--sp-bg-page)' }}>
      <div
        style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '20px 28px 64px',
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: 4,
            background: '#fff',
            border: '1px solid var(--sp-border)',
            borderRadius: 12,
            width: 'fit-content',
          }}
        >
          <SubTab
            active={tab === 'today'}
            onClick={() => setTab('today')}
            icon="clock"
            label="Today's run"
            count={attentionCount}
          />
          <SubTab
            active={tab === 'history'}
            onClick={() => setTab('history')}
            icon="chart"
            label="History"
          />
        </div>

        {tab === 'today' && (
          <>
            {statusLoading && !status && (
              <div
                className="card"
                style={{
                  padding: '40px 16px',
                  textAlign: 'center',
                  color: 'var(--sp-fg-3)',
                  fontSize: 14,
                  borderRadius: 18,
                }}
              >
                <span
                  className="tt-spinner sm"
                  style={{ marginRight: 8, verticalAlign: 'middle' }}
                />
                Loading today's run…
              </div>
            )}
            {statusError && (
              <div
                className="card"
                style={{
                  padding: '24px 20px',
                  borderRadius: 14,
                  color: '#B30745',
                  fontSize: 14,
                }}
              >
                {statusError}
              </div>
            )}
            {status && (
              <>
                <StatusHero
                  status={status}
                  onRunNow={onRunNow}
                  runBusy={runBusy}
                  autoRefreshing={autoRefreshing}
                />
                <TodayQueueSection
                  status={status}
                  departmentByEmail={departmentByEmail}
                  onRowClick={setCellDetail}
                />
              </>
            )}
          </>
        )}

        {tab === 'history' && (
          <section
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <MonthPicker
                year={year}
                month={month}
                onChange={(y, m) => {
                  setYear(y);
                  setMonth(m);
                }}
                minYear={floor.y}
                minMonth={floor.m}
                maxYear={today.getFullYear()}
                maxMonth={today.getMonth()}
              />
              <div style={{ flex: 1 }} />
              <LayoutSwitch layout={layout} onChange={setLayout} />
            </div>
            {historyLoading && (
              <div
                className="card"
                style={{
                  padding: '40px 16px',
                  textAlign: 'center',
                  color: 'var(--sp-fg-3)',
                  fontSize: 14,
                  borderRadius: 14,
                }}
              >
                <span
                  className="tt-spinner sm"
                  style={{ marginRight: 8, verticalAlign: 'middle' }}
                />
                Loading history…
              </div>
            )}
            {historyError && !historyLoading && (
              <div
                className="card"
                style={{
                  padding: '24px 20px',
                  borderRadius: 14,
                  color: '#B30745',
                  fontSize: 14,
                }}
              >
                {historyError}
              </div>
            )}
            {!historyLoading && !historyError && history && (
              layout === 'heatmap' ? (
                <MonthlyHeatmap
                  history={history}
                  year={year}
                  month={month}
                  todayIso={todayIso}
                  onCellClick={setCellDetail}
                />
              ) : (
                <MonthlyTable
                  history={history}
                  year={year}
                  month={month}
                  todayIso={todayIso}
                  onCellClick={setCellDetail}
                />
              )
            )}
          </section>
        )}
      </div>

      <ConfirmDialog
        open={!!confirm}
        {...(confirm || {})}
        busy={runBusy}
        onCancel={() => {
          if (!runBusy) setConfirm(null);
        }}
      />
      <CellDetailDialog cell={cellDetail} onClose={() => setCellDetail(null)} />
    </div>
  );
}

// Pure helpers exported for unit tests.
export { normaliseEmployee, pivotHistory };
